// Listy w nowym wyglądzie (v2 = "color", v2 uproszczony = "simple"), w stylu raportu v2.
// Handoff designera zawiera dla listów tylko odwzorowanie obecnego PDF, więc to przeniesienie stylu
// raportu (do akceptacji designera). Dane i sekcje (FUL_SECTIONS / CL_SECTIONS) jak w generatorach v1.
import { Document, Page, Text, View } from "@react-pdf/renderer";
import { FollowUpLetterDto, FollowUpLetterFile, FulParticipant, LetterDto } from "../../../../types/Rows";
import { DesignVariant, fmt, makeKit, pageStyle } from "./pdfV2Kit";

const answerText = (val?: string) => (val === "yes" ? "TAK" : val === "no" ? "NIE" : val === "na" ? "NA" : "");

export const FollowUpLetterDocumentV2 = ({
  variant,
  followUpLetter: ful,
  author,
  footerText,
  versionText,
  sections,
  attachments = [],
  participants = [],
}: {
  variant: DesignVariant;
  followUpLetter: FollowUpLetterDto;
  author: string;
  footerText?: string;
  versionText?: string;
  sections?: Set<string>;
  attachments?: FollowUpLetterFile[];
  participants?: FulParticipant[];
}) => {
  const k = makeKit(variant);
  const show = (key: string) => !sections || sections.has(key);
  const today = fmt(new Date());
  const protocol = [ful.protocolNumber, ful.studyAcronym ? `(${ful.studyAcronym})` : ""].filter(Boolean).join(" ");
  const declared = ful.declaredParticipants ?? 0;
  const randomized = ful.amountOfRandomized ?? 0;

  return (
    <Document>
      <Page size="A4" style={pageStyle}>
        <k.PageHeader />

        <k.Title
          badge={`List follow-up nr ${ful.followUpLetterNumber}`}
          note={`Data: ${today}`}
          overline={`List follow-up nr ${ful.followUpLetterNumber} · ${today}`}
          heading={protocol || ful.studyName}
        />
        {show("header") && (
          <k.MetaBlock
            items={[
              ["Sponsor", ful.sponsor || "—"],
              ["Ośrodek", ful.resortName || "—"],
              ["Wizyta", [ful.visitType, ful.visitDate ? fmt(ful.visitDate) : ""].filter(Boolean).join(" · ") || "—"],
              ["Badanie", ful.studyName || "—"],
              ["Numer protokołu", ful.protocolNumber || "—"],
              ["Akronim badania", ful.studyAcronym || "—"],
            ]}
          />
        )}

        {show("introduction") && (
          <View style={{ marginTop: 16 }}>
            {!!ful.introductionText && <k.Paragraph>{ful.introductionText}</k.Paragraph>}
            <k.Paragraph>
              {`Serdecznie dziękuję Państwu za udział w wizycie ${ful.visitType ?? ""}, która odbyła się ${fmt(ful.visitDate)}, oraz przygotowanie dokumentacji do weryfikacji.`}
            </k.Paragraph>
          </View>
        )}

        {show("participants") && (
          <View>
            <k.SectionHeader title="Uczestnicy wizyty" />
            <k.LabeledRows rows={[["CRO", ful.croParticipants], ["Ośrodek", ful.resortParticipants]]} />
          </View>
        )}

        {show("study-progress") && (
          <View>
            <k.SectionHeader title="Omówione zagadnienia" />
            <k.Muted style={{ marginBottom: 4 }}>Podczas wizyty omówiono / zweryfikowano następujące zagadnienia:</k.Muted>
            {ful.studyProgress?.trim() ? (
              k.linesOf(ful.studyProgress).map((line, i) => (
                <Text key={i} style={{ marginBottom: 2 }}>{line}</Text>
              ))
            ) : (
              // jak w v1 (SR-042): odpowiedzi „Follow up”, gdy pole „Postęp badania” jest puste
              (ful.answers ?? []).map((a, i) => (
                <View key={i} style={{ marginBottom: 4 }} wrap={false}>
                  <Text>{`${i + 1}. ${a.description}`}</Text>
                  <k.Muted>{[answerText(a.answer), a.comment].filter(Boolean).join(" – ")}</k.Muted>
                </View>
              ))
            )}
          </View>
        )}

        {show("status-osrodka") && (
          <View>
            <k.SectionHeader title="Status ośrodka" />
            <k.SiteStatusBlock
              data={{
                recruitmentPct: declared > 0 ? Math.round((randomized / declared) * 100) : 0,
                randomized,
                declared,
                protocolDeviation: ful.protocolDeviation ?? 0,
                sae: ful.reportedSAE ?? 0,
                columns: 5,
                counts: [
                  ["Zadeklarowani", ful.declaredParticipants],
                  ["W screeningu", ful.amountForScreening],
                  ["Zrandomizowani", ful.amountOfRandomized],
                  ["Niepowodzenia screeningu", ful.screenFailure],
                  ["W trakcie leczenia", ful.duringTreatment],
                ],
              }}
            />
          </View>
        )}

        {show("patient-list") && participants.length > 0 && (
          <View>
            <k.SectionHeader title="Zakres monitorowanych danych" />
            <k.MonitoredDataTable
              rows={participants.map((p) => ({
                participantNumber: p.participantNumber,
                visitNumber: p.visitNumber,
                sdv: !!p.isFinshed,
                rbv: !!p.isVerified,
                comment: p.comment,
              }))}
            />
          </View>
        )}

        {show("podsumowanie") && (
          <View>
            <k.SectionHeader title="Podsumowanie" />
            <k.Paragraph>{ful.finalComment || "—"}</k.Paragraph>
          </View>
        )}

        {show("signature") && <k.LetterSignature author={author} />}

        {show("attachments") && attachments.length > 0 && (
          <View>
            <k.SectionHeader title="Załączniki" />
            {attachments.map((f, i) => (
              <Text key={f.id} style={{ marginBottom: 2 }}>{`${i + 1}. ${f.label?.trim() || f.name}`}</Text>
            ))}
          </View>
        )}

        <k.PageFooter left={footerText} right={versionText ?? ""} />
      </Page>
    </Document>
  );
};

export const ConfirmationLetterDocumentV2 = ({
  variant,
  confirmationLetter: cl,
  author,
  footerText,
  versionText,
  title,
  visitType,
  sections,
}: {
  variant: DesignVariant;
  confirmationLetter: LetterDto;
  author: string;
  footerText?: string;
  versionText?: string;
  title: string;
  visitType: string;
  sections?: Set<string>;
}) => {
  const k = makeKit(variant);
  const show = (key: string) => !sections || sections.has(key);
  const today = fmt(new Date());

  return (
    <Document>
      <Page size="A4" style={pageStyle}>
        <k.PageHeader />

        <k.Title
          badge="List potwierdzający wizytę"
          note={`Data: ${today}`}
          overline={`List potwierdzający wizytę w ośrodku · ${today}`}
          heading={title || cl.studyName}
        />
        {show("header") && (
          <k.MetaBlock
            items={[
              ["Typ wizyty", visitType || "—"],
              ["Główny badacz", cl.mainResearcher || "—"],
              ["Numer protokołu", cl.protocolNumber || "—"],
              ["Numer ośrodka", cl.resortNumber || "—"],
              ["Data wizyty", cl.visitDay || "—"],
              ["Rozpoczęcie", cl.visitHour ? `ok. godz. ${cl.visitHour}` : "—"],
            ]}
          />
        )}

        {show("main-text") && (
          <View style={{ marginTop: 16 }}>
            {!!cl.introductionText && <k.Paragraph>{`${cl.introductionText},`}</k.Paragraph>}
            <k.Paragraph>
              {`niniejszy list ma na celu potwierdzić wizytę ${cl.visitType ?? ""} w Ośrodku w dn. ${cl.visitDay ?? ""}. Planuję rozpocząć wizytę ok. godz. ${cl.visitHour ?? ""}.`}
            </k.Paragraph>
          </View>
        )}

        {show("research-team") && (
          <View>
            <k.SectionHeader title="Obecni członkowie zespołu" />
            <k.Muted>Podczas wizyty powinni być obecni Członkowie Zespołu, a w szczególności:</k.Muted>
            <k.BulletList items={k.linesOf(cl.researchTeam)} />
          </View>
        )}

        {show("visit-reason") && (
          <View>
            <k.SectionHeader title="Wymagany dostęp" />
            <k.Muted>Podczas wizyty będę potrzebować dostępu do:</k.Muted>
            <k.BulletList items={k.linesOf(cl.visitReason)} />
          </View>
        )}

        {show("cro-team") && (
          <View>
            <k.SectionHeader title="Kontakt z zespołem CRO" />
            <k.Muted>W przypadku jakichkolwiek pytań proszę o kontakt z naszym zespołem CRO:</k.Muted>
            {k.linesOf(cl.croTeam).map((line, i) => (
              <Text key={i} style={{ marginTop: 2 }}>{line}</Text>
            ))}
          </View>
        )}

        {show("signature") && <k.LetterSignature author={author} />}

        <k.PageFooter left={footerText} right={versionText ?? ""} />
      </Page>
    </Document>
  );
};
